import React, { useEffect, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {ConnectedWork} from "@/components/Opportunity";
import JsonLd from "@/components/JsonLd";
import { trackPageView, trackContactClick } from "@/lib/analytics";
import {
  organizationSchema,
  websiteSchema,
  localBusinessSchema,
  routeSchemas,
} from "@/lib/structuredData";

export default function Layout() {
  const location = useLocation();

  // Sitewide schemas — computed once, never change between routes
  const sitewideSchemas = useMemo(
    () => [organizationSchema(), websiteSchema(), localBusinessSchema()],
    []
  );
  // Breadcrumb schema — recomputed per route
  const breadcrumbSchemas = useMemo(
    () => routeSchemas(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    // Scroll to top on route change, but preserve hash anchors
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      setTimeout(() => {
        const el = document.getElementById(location.hash.slice(1));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  }, [location.pathname, location.hash]);

  // GA4 SPA pageview — fired after the route's <useDocumentMeta> updates document.title
  useEffect(() => {
    const id = setTimeout(() => trackPageView(), 0);
    return () => clearTimeout(id);
  }, [location.pathname, location.search]);

  // Track contact intent independently of confirmed form submissions.
  useEffect(() => {
    const onContactClick = (event) => {
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      const href = anchor?.getAttribute('href') || '';
      if (/^tel:/i.test(href)) trackContactClick('phone');
      if (/^mailto:/i.test(href)) trackContactClick('email');
    };
    document.addEventListener('click', onContactClick);
    return () => document.removeEventListener('click', onContactClick);
  }, []);

  // Global reveal-on-scroll observer for any element with `.reveal`
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const els = document.querySelectorAll(".reveal:not(.is-visible)");
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -6% 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [location.pathname]);

  return (
    <div data-testid="site-layout" className="min-h-screen flex flex-col bg-[#050505] text-white">
      <JsonLd id="forgedops-sitewide-schema" schemas={sitewideSchemas} />
      <JsonLd id="forgedops-breadcrumbs" schemas={breadcrumbSchemas} />
      <Navbar />
      <main tabIndex={-1} id="main-content" className="flex-1 pt-20">
        <Outlet />
<ConnectedWork path={location.pathname}/>
      </main>
      <Footer />
    </div>
  );
}


